import create, { StoreApi, UseBoundStore } from "zustand";
import { ExtTaskData } from "../../../../cuba/entities/base/tsadv_ExtTaskData";
import { restServices } from "../../../../cuba/services";
import { BprocFormData } from "../../../../cuba/entities/bproc/bproc_FormData";
import { ProcessInstanceData } from "../../../../cuba/entities/base/bproc_ProcessInstanceData";
import { rootStore } from "../../../store";
import { SerializedEntity } from "@cuba-platform/rest";
import { loadStartForm, loadTaskForm } from "../lib/bpm-load";
import { findMyActiveTask } from "../lib/find-my-active-task";
import { createModalStore, ModalStore } from "./modal";
import { BpmRolesDefiner } from "cuba/entities/base/tsadv$BpmRolesDefiner";
import { NotPersisitBprocActors } from "cuba/entities/base/tsadv_NotPersisitBprocActors";
import { queryClient } from "../../../../index";

const STANDARD_SERVICE_NAME = "bprocService";

type TaskService = {
  tasks: (args: {
    processInstanceData: ProcessInstanceData;
  }) => Promise<SerializedEntity<ExtTaskData>[]>;
};

type RestServices = typeof restServices & {
  [key: string]: TaskService;
};

export type BpmState = {
  // keys
  customKeyForTaskService: string | null;
  processDefinitionKey: string | null;
  excludeOutcomes: string[] | null;
  processInstanceBusinessKey: string | null;
  isNeedBPM: boolean;

  // task
  tasks: ExtTaskData[] | null;
  activeUserTask: ExtTaskData | null;
  activeTask: ExtTaskData | null;
  isUserInitiator: boolean | null;

  // additional task
  activeTaskAdditional: ExtTaskData | null;

  //actors
  bprocRolesDefiner: BpmRolesDefiner | null;
  notPersisitBprocActorsFiltered:
    | SerializedEntity<NotPersisitBprocActors>[]
    | null;

  // form
  formData: BprocFormData | null;
  isStartForm: boolean;
  processInstanceData: ProcessInstanceData | null;

  //modalStore
  modalStore: UseBoundStore<ModalStore, StoreApi<ModalStore>>;

  // actions
  getProcessInstanceData: (
    processInstanceBusinessKey: string,
    processDefinitionKey: string,
  ) => Promise<void>;

  initVariablesByBproc: () => void;
  setKeys: (args: {
    processDefinitionKey?: string;
    processInstanceBusinessKey?: string;
  }) => void;

  loadBprocRolesDefiner: (
    request: SerializedEntity<any>,
    employeePersonGroupId: string,
  ) => Promise<BprocRolesDefiner | null>;

  loadNotPersisitBprocActors: (
    request: SerializedEntity<any>,
    rolesDefiner: BprocRolesDefiner,
    employeePersonGroupId: string,
  ) => Promise<SerializedEntity<NotPersisitBprocActors>[] | null>;
};

export type CreateBpmStoreArgs = {
  processInstanceBusinessKey: string;
  processDefinitionKey: string;
  isNeedBPM?: boolean;
  customKeyForTaskService?: string;
  excludeOutcomes?: string[];
};

class BprocRolesDefiner {}

export const createBpmStore = ({
                                 processInstanceBusinessKey,
                                 processDefinitionKey,
                                 customKeyForTaskService,
                                 excludeOutcomes,
                                 isNeedBPM = true,
                               }: CreateBpmStoreArgs) =>
  create<BpmState>((set, get) => {
    const modalStore = createModalStore();

    return {
      //keys
      customKeyForTaskService: customKeyForTaskService ?? null,
      processDefinitionKey: processDefinitionKey ?? null,
      excludeOutcomes: excludeOutcomes ?? null,
      processInstanceBusinessKey: processInstanceBusinessKey ?? null,
      isNeedBPM: isNeedBPM,

      //task
      tasks: null,
      activeUserTask: null,
      activeTask: null,
      isUserInitiator: null,

      //additional task
      activeTaskAdditional: null,

      //form
      formData: null,
      isStartForm: false,
      processInstanceData: null,

      //actors
      bprocRolesDefiner: null,
      notPersisitBprocActorsFiltered: null,

      //modal
      modalStore,

      //actions
      // TODO нужно добавить кастомную функцию
      initVariablesByBproc: () => {
        return;
      },

      setKeys: ({ processInstanceBusinessKey, processDefinitionKey }) => {
        set({
          processInstanceBusinessKey:
            processInstanceBusinessKey ?? get().processInstanceBusinessKey,
          processDefinitionKey:
            processDefinitionKey ?? get().processDefinitionKey,
        });
      },

      getProcessInstanceData: async (
        processInstanceBusinessKey,
        processDefinitionKey,
      ) => {
        if (!get().isNeedBPM) return;
        const excludeOutcomes = get().excludeOutcomes;
        const USER_ID = rootStore.userInfo.id;
        set({ formData: { outcomes: [] }, isStartForm: false });

        if (processInstanceBusinessKey === "new") {
          const formData = await loadStartForm(
            processDefinitionKey,
            excludeOutcomes,
          );
          set({ formData, isStartForm: true });
          get().initVariablesByBproc();
          return;
        }

        set({ isStartForm: false });
        get().initVariablesByBproc();

        const processInstanceData = await queryClient.fetchQuery({
          queryKey: [
            "processInstanceData",
            processInstanceBusinessKey,
            processDefinitionKey,
          ],
          queryFn: () =>
            restServices.bprocService.processInstanceData({
              processInstanceBusinessKey,
              processDefinitionKey,
            }),
        });

        set({
          isUserInitiator:
            processInstanceData.startUserId === rootStore.userInfo.id,
        });

        if (!processInstanceData) return set({ isStartForm: true });

        const isUser = processInstanceData.startUserId === USER_ID;
        const serviceKey =
          get().customKeyForTaskService || STANDARD_SERVICE_NAME;
        const service = (restServices as RestServices)[serviceKey];

        const tasks = await service.tasks({ processInstanceData });
        const activeTask = tasks.find((task) => !task.endTime);
        const activeTaskAdditional = findMyActiveTask(
          activeTask?.childs,
          USER_ID,
        );

        const activeUserTask = activeTask?.assigneeOrCandidates?.some(
          (user) => user.id === USER_ID,
        )
          ? activeTask
          : null;

        if (activeTaskAdditional?.outcome !== "TASK_CLOSED") {
          const formData = await loadTaskForm(
            processInstanceData.id,
            excludeOutcomes,
          );
          set({ formData, isStartForm: false });
          get().initVariablesByBproc();
        }

        if (activeUserTask) {
          const formData = await loadTaskForm(
            activeUserTask.id,
            excludeOutcomes,
          );
          set({ formData, isStartForm: false });
          get().initVariablesByBproc();
        } else if (isUser && activeTask) {
          const formData = excludeOutcomes?.includes("CANCEL")
            ? { outcomes: [] }
            : { outcomes: [{ id: "CANCEL" }] };
          set({ formData, isStartForm: false });
          get().initVariablesByBproc();
        }

        set({
          tasks,
          activeTask,
          activeTaskAdditional,
          activeUserTask,
          processInstanceData,
        });
      },

      loadBprocRolesDefiner: async (
        request: SerializedEntity<any>,
        employeePersonGroupId: string,
      ): Promise<BprocRolesDefiner> => {
        if (!get().isNeedBPM) return {};
        const value = await restServices.startBprocService.getBpmRolesDefiner({
          request,
          employeePersonGroupId,
          isAssistant: rootStore.assistantTeamInfo.active,
        });

        set({ bprocRolesDefiner: value });
        return value;
      },

      loadNotPersisitBprocActors: async (
        request: SerializedEntity<any>,
        rolesDefiner: BprocRolesDefiner,
        employeePersonGroupId: string,
      ): Promise<SerializedEntity<NotPersisitBprocActors>[]> => {
        if (!get().isNeedBPM) return [];
        const actors =
          await restServices.startBprocService.getNotPersisitBprocActors({
            employeePersonGroupId,
            bpmRolesDefiner: rolesDefiner,
            isAssistant: rootStore.assistantTeamInfo.active,
            request,
          });

        const filtered = actors.filter(
          (actor) => actor.users && actor.users.length > 0,
        );

        set({ notPersisitBprocActorsFiltered: filtered });
        return filtered;
      },
    };
  });
